import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { fetchFeatures } from "../../utils/fetchFeatures";
import { jwtDecode } from "jwt-decode";
import ButtonBack from "../../utils/ButtonBack";
import { PhoneInput } from "react-international-phone";
import ErrorModal from "../../utils/ErrorModal";
import CountrySelect from "../../utils/countrySelect";
import {
  getCountryCallingCode,
  parsePhoneNumber,
} from "react-phone-number-input";
import { useOrganization } from "../../utils/OrganizationContext";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ThemedDatePicker from "../../utils/ThemedDatePicker";
import ThemedSelect from "../../utils/ThemedSelect";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#DDE4EC";
const FIELD_BG = "#F8FAFC";
const PRIMARY = "#0B5C8A";
const PRIMARY_DARK = "#084767";
const ERROR = "#C42B24";

// Section accent dots (multi-hue stages) — same as Add lead
const SEC_COLORS = {
  contact: "#2F7FD1",
  assignment: "#12A08F",
  requirement: "#E0911B",
  followup: "#6D5DE8",
  attribution: "#D6457A",
  custom: "#8795A8",
};

// Scoped theme: every MUI Select / Autocomplete / Checkbox / menu on this
// form picks up the Direction C look without per-element overrides.
const formTheme = createTheme({
  palette: {
    primary: { main: PRIMARY, dark: PRIMARY_DARK, contrastText: "#FFFFFF" },
    error: { main: ERROR },
    text: { primary: INK, secondary: "#4A5A6E" },
  },
  shape: { borderRadius: 8 },
  typography: { fontFamily: FONT, button: { textTransform: "none", fontWeight: 600 } },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          background: FIELD_BG,
          fontFamily: FONT,
          fontSize: 13.5,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: FIELD_EDGE },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#B4C6DA" },
          "&.Mui-focused": { background: "#FFFFFF" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY, borderWidth: 1 },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: ERROR },
        },
        input: { "&::placeholder": { color: "#8A97A8", opacity: 1 } },
      },
    },
    MuiSvgIcon: { styleOverrides: { root: { "&.MuiSelect-icon, &.MuiAutocomplete-popupIndicator svg": { fontSize: 18, color: "#8A97A8" } } } },
    MuiFormHelperText: { styleOverrides: { root: { display: "none" } } },
    MuiMenu: {
      styleOverrides: {
        paper: { marginTop: 4, borderRadius: 12, border: `1px solid ${LINE}`, boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)" },
        list: { padding: 4 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: 13.5,
          borderRadius: 8,
          minHeight: 36,
          "&.Mui-selected, &.Mui-selected:hover": { background: "#EAF2FB" },
        },
      },
    },
    MuiListItemText: { styleOverrides: { primary: { fontFamily: FONT, fontSize: 13.5 } } },
    MuiCheckbox: { styleOverrides: { root: { color: "#C3CDD9", padding: 6, "&.Mui-checked": { color: PRIMARY } } } },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { marginTop: 4, borderRadius: 12, border: `1px solid ${LINE}`, boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)" },
        option: { fontFamily: FONT, fontSize: 13.5, borderRadius: 8, marginInline: 6 },
        groupLabel: { fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: MUTED, lineHeight: "30px" },
      },
    },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 18, boxShadow: "0 32px 64px -20px rgba(15,27,42,0.5)" } } },
    MuiBackdrop: { styleOverrides: { root: { "&:not(.MuiBackdrop-invisible)": { background: "rgba(15,27,42,0.46)" } } } },
  },
});

/* ---------- shared visual styles (values only — no logic) ---------- */
const card = {
  background: "#FFFFFF",
  border: `1px solid ${LINE}`,
  borderRadius: 14,
  marginBottom: 14,
  scrollMarginTop: 90,
};
const cardHead = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "13px 16px",
  borderBottom: `1px solid ${LINE}`,
};
const cardTitle = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  fontFamily: FONT,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.11em",
  textTransform: "uppercase",
  color: INK_2,
};
const dot = (c, size = 6) => ({
  width: size,
  height: size,
  flex: `0 0 ${size}px`,
  borderRadius: "50%",
  background: c,
});
const cardMeta = { fontFamily: FONT, fontSize: 12, color: MUTED };
const lbl = {
  display: "block",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  marginBottom: 6,
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 600,
  color: INK,
};
const req = { color: ERROR, marginLeft: 3 };
const inp = (err) => ({
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  height: 36,
  padding: "0 12px",
  margin: 0,
  background: FIELD_BG,
  border: `1px solid ${err ? ERROR : FIELD_EDGE}`,
  borderRadius: 8,
  fontFamily: FONT,
  fontSize: 13.5,
  color: INK,
  outline: "none",
});
const readOnlyInp = {
  ...inp(false),
  background: "#F1F4F8",
  color: MUTED,
  border: `1px dashed ${FIELD_EDGE}`,
  cursor: "not-allowed",
};
const errText = {
  display: "block",
  margin: "4px 0 0",
  fontFamily: FONT,
  fontSize: 11.5,
  fontWeight: 500,
  color: ERROR,
};
const acSx = {
  margin: 0,
  "& .MuiInputBase-root": {
    minHeight: 36,
    fontSize: "13.5px",
    borderRadius: "8px",
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "8px !important",
  },
  "& .MuiInputBase-input": { padding: "7px 4px !important", minWidth: "0 !important" },
};
const selSx = {
  display: "flex",
  minHeight: 36,
  borderRadius: "8px",
  fontSize: "13.5px",
  margin: 0,
  background: FIELD_BG,
  "& .MuiSelect-select": {
    padding: "7px 12px",
    minHeight: "unset !important",
  },
};
const boxWrap = (err) => ({
  display: "flex",
  alignItems: "center",
  height: 36,
  boxSizing: "border-box",
  background: FIELD_BG,
  border: `1px solid ${err ? ERROR : FIELD_EDGE}`,
  borderRadius: 8,
  overflow: "hidden",
});
const prefix = {
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  height: "100%",
  padding: "0 2px 0 12px",
  fontFamily: MONO,
  fontSize: 13,
  color: MUTED,
};
const bareInp = {
  flex: 1,
  minWidth: 0,
  height: "100%",
  boxSizing: "border-box",
  padding: "0 12px 0 8px",
  margin: 0,
  border: 0,
  background: "transparent",
  fontFamily: MONO,
  fontSize: 13,
  color: INK,
  outline: "none",
};
const waBox = (on) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  height: 36,
  boxSizing: "border-box",
  padding: "0 12px",
  background: on ? "#E9F7EE" : FIELD_BG,
  border: `1px solid ${on ? "#C4E8D2" : FIELD_EDGE}`,
  borderRadius: 8,
  cursor: "pointer",
  fontFamily: FONT,
  fontSize: 13.5,
  fontWeight: on ? 600 : 500,
  color: on ? "#1B6B41" : INK_2,
  userSelect: "none",
});
const waTick = (on) => ({
  width: 14,
  height: 14,
  flex: "0 0 14px",
  borderRadius: 3,
  boxSizing: "border-box",
  border: `1.5px solid ${on ? "#2FA864" : "#9AA7B8"}`,
  background: on ? "#2FA864" : "#FFFFFF",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#FFFFFF",
  fontSize: 10,
  fontWeight: 800,
  lineHeight: 1,
});
const primaryBtn = {
  height: 38,
  px: 2,
  margin: "0 !important",
  borderRadius: "10px !important",
  background: `${PRIMARY} !important`,
  color: "#FFFFFF !important",
  border: "0 !important",
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  whiteSpace: "nowrap",
  "&:hover": { background: `${PRIMARY_DARK} !important` },
  "&.Mui-disabled": { opacity: 0.6 },
};
const ghostBtn = {
  height: 38,
  px: 2,
  borderRadius: "10px",
  background: "#FFFFFF",
  color: INK,
  border: `1px solid ${FIELD_EDGE}`,
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 600,
  textTransform: "none",
  "&:hover": { background: "#F4F7FA" },
};
const historyCard = {
  background: "#FFFFFF",
  border: `1px solid ${LINE}`,
  borderLeft: "3px solid #2FA864",
  borderRadius: 12,
  padding: "12px 14px",
  marginBottom: 10,
  fontFamily: FONT,
};
// Every card uses the same 5 equal columns so fields line up vertically
// from card to card (Contact included).
const SHARED_GRID = "repeat(5, minmax(0,1fr))";
const cols = (n, lgTemplate) => ({
  display: "grid",
  gap: "16px 12px",
  gridTemplateColumns: {
    xs: "minmax(0,1fr)",
    sm: "repeat(2, minmax(0,1fr))",
    lg: lgTemplate || `repeat(${n}, minmax(0,1fr))`,
  },
});

// Scoped resets so global App.css rules (form cards, inputs, labels)
// can't squeeze or restyle this page.
const SCOPED_CSS = `
.dc-editlead .dc-card{width:100%!important;max-width:none!important;margin:0 0 14px 0!important;padding:0!important;box-shadow:none!important;box-sizing:border-box!important;}
.dc-editlead .dc-card label{width:auto!important;margin-top:0!important;font-size:12px!important;line-height:1.4!important;}
.dc-editlead .dc-card input:not([type=checkbox]),.dc-editlead .dc-card textarea{width:100%!important;max-width:none!important;margin:0!important;box-sizing:border-box!important;font-size:13.5px!important;}
.dc-editlead .dc-card input:not([type=checkbox]){height:36px!important;}
.dc-editlead .dc-card .MuiInputBase-root input{height:auto!important;}
.dc-editlead .dc-card input.dc-bare{height:100%!important;border:0!important;background:transparent!important;flex:1 1 auto;min-width:0;}
.dc-editlead .dc-card label.dc-wa{display:flex!important;height:36px!important;margin:0!important;font-size:13.5px!important;overflow:hidden;}
.dc-editlead .dc-card input::placeholder,.dc-editlead .dc-card textarea::placeholder{color:#8A97A8;opacity:1;}
.dc-editlead .dc-cc *{font-family:'Plus Jakarta Sans',system-ui,sans-serif!important;font-size:13.5px!important;color:#0F1B2A;}
.dc-editlead .dc-card input.dc-bare,.dc-editlead .dc-card input.dc-bare::placeholder{font-family:'Plus Jakarta Sans',system-ui,sans-serif!important;}
.dc-editlead .dc-card .MuiAutocomplete-root,.dc-editlead .dc-card .MuiInputBase-root,.dc-editlead .dc-card .MuiSelect-root{width:100%!important;max-width:none!important;}
`;

const staticFields = [
  { name: "name", label: "Lead Name", type: "text" },
  { name: "phone", label: "Phone", type: "text" },
  { name: "isWhatsapp", label: "is on WhatsApp", type: "boolean" },
  { name: "email", label: "Email", type: "email" },
  { name: "alternatePhone", label: "Alternate Phone", type: "text" },
  {
    name: "isAlternateWhatsapp",
    label: "is on WhatsApp",
    type: "boolean",
  },

  { name: "leadSource", label: "Lead Source", type: "text" },

  { name: "currentCity", label: "Current City", type: "text" },
  { name: "currentCountry", label: "Current Country", type: "text" },

  { name: "requirement", label: "Requirement", type: "text" },

  {
    name: "preferredPropertyType",
    label: "Preferred Property Type",
    type: "text",
  },

  { name: "minBudget", label: "Minimum Budget", type: "number" },
  { name: "maxBudget", label: "Maximum Budget", type: "number" },

  { name: "timelineToBuy", label: "Timeline To Buy", type: "text" },
  { name: "purpose", label: "Purpose", type: "text" },

  // DATE FIELDS
  { name: "nextFollowUp", label: "Next Follow Up", type: "date" },
  { name: "lastFollowUp", label: "Last Follow Up", type: "date" },

  // UTM FIELDS
  { name: "utmSource", label: "UTM Source", type: "text" },
  { name: "utmMedium", label: "UTM Medium", type: "text" },
  { name: "utmCampaign", label: "UTM Campaign", type: "text" },
  { name: "utmTerm", label: "UTM Term", type: "text" },
  { name: "utmContent", label: "UTM Content", type: "text" },

  { name: "leadScore", label: "Lead Score", type: "number" },
  { name: "aiScore", label: "AI Score", type: "number" },
];

const PRIORITY_OPTIONS = [
  { value: "LOW", label: "Low" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HIGH", label: "High" },
];

const CONTACT_FIELDS = [
  "name",
  "phone",
  "isWhatsapp",
  "email",
  "alternatePhone",
  "isAlternateWhatsapp",
];
const REQUIREMENT_FIELDS = [
  "requirement",
  "preferredPropertyType",
  "minBudget",
  "maxBudget",
  "timelineToBuy",
  "purpose",
];
const UTM_FIELDS = [
  "utmSource",
  "utmMedium",
  "utmCampaign",
  "utmTerm",
  "utmContent",
];

// Placeholders from the Direction C mock (display only)
const PLACEHOLDERS = {
  name: "Full name",
  email: "name@example.com",
  requirement: "Residential — buy",
  preferredPropertyType: "2 BHK, 3 BHK…",
  timelineToBuy: "1–3 months",
  purpose: "End use",
  currentCity: "City",
  currentCountry: "Country",
};

const EditLead = () => {
  const { leadId } = useParams();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const subs = JSON.parse(localStorage.getItem("subs"));
  const [access, setAccess] = useState();
  const [formData, setFormData] = useState({});
  const [inputFields, setInputFields] = useState([]);
  const [partners, setPartners] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [inventories, setInventories] = useState();
  const location = useLocation();
  // const [role, setRole] = useState();
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState();
  const [sources, setSources] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("success");
  const [phoneCountry, setPhoneCountry] = useState("IN");
  const [altPhoneCountry, setAltPhoneCountry] = useState("IN");
  const [btnLoading, setBtnLoading] = useState(false);
  const [remarks, setRemarks] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const isMobile = useMediaQuery("(max-width: 750px)");
  const hideSectionNav = useMediaQuery("(max-width: 1000px)");
  const sectionRefs = useRef({});
  const [activeSection, setActiveSection] = useState("contact");
  const orgId = JSON.parse(localStorage.getItem("org"));
  const { role } = useOrganization();

  const sortedStatus = [...(status || [])].sort((a, b) => {
    const groupA = a.metaLeadStatus?.status || "";
    const groupB = b.metaLeadStatus?.status || "";
    if (groupA !== groupB) {
      return groupA.localeCompare(groupB);
    }
    return a.status.localeCompare(b.status);
  });

  useEffect(() => {
    const fetchLeadStatus = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/lead-status/all?projectId=${formData?.projectId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setStatus(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchLeadStatus();

    const fetchSources = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/lead-source?projectId=${formData?.projectId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setSources(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchSources();
  }, [formData.projectId]);

  useEffect(() => {
    const getPartners = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/partner/all?orgId=${orgId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setPartners(res.data);
      } catch (error) {
        console.log(error.response.data.Exception || "Access denied");
      }
    };
    getPartners();
  }, [orgId]);

  const fetchLead = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/lead?leadId=${leadId}`,
      { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
    );

    const lead = res.data;
    let parsedPhone = null;

    if (lead.phone) {
      parsedPhone = parsePhoneNumber(
        lead.countryCode !== null
          ? lead.countryCode.startsWith("+")
            ? `${lead.countryCode}${lead.phone}`
            : `+${lead.countryCode}${lead.phone}`
          : `+${lead.phone}`,
      );
    }
    const populated = {
      ...lead,
      phone: parsedPhone ? parsedPhone.nationalNumber : "",
      teamId: lead.team?.teamId || "",
      projectId: lead?.projectId || "",
      assignedBy: lead?.assignedBy?.email,
      assignedTo: [
        ...(lead.isAdminAssigned ? [storedUser.username] : []),
        ...(lead.assignedTo?.map((u) => u.mobile) || []),
      ],
      inventoryIds: lead?.inventories?.map((inv) => inv.inventoryId) || [],
      status: "",
      remark: "",
      nextFollowUp: "",
    };

    lead.customFields?.forEach((cf) => {
      if (cf.type === "MULTI_SELECT") {
        populated[cf.fieldName] = cf.selectedOptions || [];
      } else if (cf.type === "SINGLE_SELECT") {
        populated[cf.fieldName] = cf.selectedOption || "";
      } else {
        populated[cf.fieldName] = cf.value || "";
      }
    });

    if (parsedPhone?.country) {
      setPhoneCountry(parsedPhone.country);
    }

    setFormData(populated);
  };

  useEffect(() => {
    fetchLead();
  }, [leadId]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // const decoded = jwtDecode(storedUser.accessToken);
        // const userRole = decoded.userRole[0];
        // setRole(userRole);
        if (role === "ADMIN") {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/project/all?orgId=${orgId}`,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
          );
          setProjects(res.data);
        } else {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/project/team/all?orgId=${orgId}`,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
          );
          setProjects(res.data);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchProjects();
  }, [orgId]);

  useEffect(() => {
    const loadUsers = async () => {
      if (!projects?.length || !formData.projectId) {
        setUsers([]);
        return;
      }

      const selectedProject = projects.find(
        (p) => p.projectId === formData.projectId,
      );

      const projectUsers = selectedProject?.team?.orgUsers || [];

      let admins = [];

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-user/admins?orgId=${orgId}&username=${storedUser.username}`,
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        );

        admins = res.data || [];
      } catch (error) {
        console.log("Error fetching admins:", error);
      }

      const assignableUsers = [...projectUsers, ...admins];

      setUsers(assignableUsers);

      // FIX: keep the lead's saved assignees. The project default is only
      // applied when the lead has nobody assigned yet.
      setFormData((prev) => {
        if (prev.assignedTo && prev.assignedTo.length > 0) return prev;

        if (
          selectedProject?.assignmentStrategy === "DIRECT" &&
          selectedProject?.defaultAssignee
        ) {
          const defaultUser = projectUsers.find(
            (user) => user.orgUserId === selectedProject.defaultAssignee,
          );

          return {
            ...prev,
            assignedTo: defaultUser ? [defaultUser.mobile] : null,
          };
        }

        return {
          ...prev,
          assignedTo: null,
        };
      });
    };

    loadUsers();
  }, [projects, formData.projectId]);

  const checkAccess = async () => {
    try {
      const hasAccess = await fetchFeatures(
        "LEAD_STRUCTURE",
        subs,
        storedUser.accessToken,
      );

      setAccess(hasAccess);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchInventories = async () => {
    if (!formData.projectId) {
      setInventories([]);
      return;
    }
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/inventory/project?projectId=${formData.projectId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setInventories(res.data || []);
    } catch (err) {
      console.error("Failed to fetch inventories", err);
    }
  };

  useEffect(() => {
    fetchInventories();
    checkAccess();
    if (subs) {
      const fetchStructure = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/lead-structure?projectId=${formData.projectId}`,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
          );
          setInputFields(res.data);
        } catch (error) {
          console.log(error);
        }
      };
      fetchStructure();
    }
  }, [formData.projectId]);

  const handleChange = (key, val) => {
    setFormData((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handlePhoneChange = (key, val) => {
    const digitsOnly = val.replace(/\D/g, "").slice(0, 10);
    handleChange(key, digitsOnly);
  };

  const handleWriteWithAI = async () => {
    try {
      setAiLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/lead/write-comment`,
        {
          leadName: formData.name,
          requirement: formData.requirement,
          preferredPropertyType: formData.preferredPropertyType,
          status: formData.status,
          existingText: formData.remark || "",
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      handleChange("remark", res.data);
    } catch (error) {
      console.log(error);
      setModalMsg(error?.response?.data?.Exception || "Failed to generate comment");
      setModalType("error");
      setModalOpen(true);
    } finally {
      setAiLoading(false);
    }
  };

  const buildCustomValues = () =>
    inputFields.customFields
      ?.map((field) => {
        const value = formData[field.fieldName];
        if (!value || (Array.isArray(value) && value.length === 0)) return null;

        if (field.type === "SINGLE_SELECT") {
          return { fieldName: field.fieldName, optionId: value };
        }

        if (field.type === "MULTI_SELECT") {
          return { fieldName: field.fieldName, optionIds: value };
        }

        return { fieldName: field.fieldName, value };
      })
      .filter(Boolean);

  const REQUIRED_FIELDS = ["name", "phone"];

  const handleEditLead = async () => {
    const isValidEmail = (email) => {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return regex.test(email);
    };

    const isValidMobile = (phone) => {
      const regex = /^\d{7,12}$/;
      return regex.test(phone);
    };

    const newErrors = {};

    // Required fields validation
    REQUIRED_FIELDS.forEach((field) => {
      if (!formData[field] || formData[field].toString().trim() === "") {
        newErrors[field] = "This field is required";
      }
    });

    if (!formData.assignedTo || formData.assignedTo.length === 0) {
      newErrors.assignedTo = "At least select one team member";
    }

    if (formData.email && !isValidEmail(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (formData.phone && !isValidMobile(formData.phone)) {
      newErrors.phone = "Enter a valid mobile number";
    }

    // if (formData.alternatePhone && !isValidMobile(formData.alternatePhone)) {
    //   newErrors.alternatePhone = "Enter a valid mobile number";
    // }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    const payload = {
      ...formData,
      orgId: orgId,
      phone: formData.phone.replace(/\D/g, ""),
      countryCode: getCountryCallingCode(phoneCountry),
      // alternatePhone: formData.alternatePhone
      //   ? `${getCountryCallingCode(altPhoneCountry)}${formData.alternatePhone.replace(/\D/g, "")}`
      //   : null,
      alternatePhone: formData.alternatePhone
        ? `${formData.alternatePhone.replace(/\D/g, "")}`
        : null,
      alternateCountryCode: formData.alternatePhone
        ? getCountryCallingCode(altPhoneCountry)
        : null,
      assignedTo: formData.assignedTo,
      customValues: buildCustomValues(),
    };
    try {
      setBtnLoading(true);
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/lead?leadId=${leadId}`,
        payload,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );

      if (formData.status || formData.nextFollowUp || formData.remark) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/lead/update-lead-status`,
          {
            username: storedUser.username,
            leadId: leadId,
            lastFollowUp: new Date().toISOString().split("T")[0],
            nextFollowUp: formData.nextFollowUp || null,
            remark: formData.remark || null,
            status: formData.status || null,
          },
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
      }

      await fetchLead();
      await fetchData();

      setModalMsg("Lead updated successfully!");
      setModalType("success");
      setModalOpen(true);
    } catch (error) {
      console.error("Failed to update lead", error);
      setModalMsg("Failed to update lead. Please try again.");
      setModalType("error");
      setModalOpen(true);
    } finally {
      setBtnLoading(false);
    }
  };

  const staticFieldMap = staticFields.reduce((a, f) => {
    a[f.name] = f;
    return a;
  }, {});

  const orderedFieldNames = staticFields
    .map((f) => f.name)
    .filter(
      (name) =>
        Array.isArray(inputFields?.fieldNames) &&
        inputFields.fieldNames.includes(name),
    );

  useEffect(() => {
    if (users && users.length === 0) {
      // FIX: only fall back to "Self" when nobody is assigned yet
      setFormData((prev) =>
        prev.assignedTo && prev.assignedTo.length > 0
          ? prev
          : { ...prev, assignedTo: [storedUser.username] },
      );
    }
  }, [users]);

  const leadScoreOptions = [
    {
      label: "Excellent",
      options: [100, 90, 80],
    },
    {
      label: "Good",
      options: [70, 60, 50],
    },
    {
      label: "Warm",
      options: [50, 40, 30],
    },
    {
      label: "Cold",
      options: [20, 10, 0],
    },
  ].flatMap((group) =>
    group.options.map((score) => ({
      group: group.label,
      value: score,
    })),
  );

  const fetchData = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/history?leadId=${leadId}`,
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
        },
      );

      // Sort newest first
      const sorted = res.data.sort(
        (a, b) => new Date(b.createdDate) - new Date(a.createdDate),
      );

      setRemarks(sorted);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    fetchData();
  }, [leadId]);

  /* ---------- visibility (same rules as before) ---------- */
  // Free form (!access) shows every static field; structured form (access)
  // shows only fields present in the project's lead structure.
  const show = (name) => !access || orderedFieldNames.includes(name);
  // City / Country were always structure-driven in both forms.
  const showProfile = (name) => orderedFieldNames.includes(name);

  const backPath = `/dashboard/lead/${leadId}`;
  const goBack = () =>
    navigate(
      role === "ADMIN" ? `/dashboard/lead/${leadId}` : `/dashboard/lead/${leadId}`,
    );

  const selectedProject = projects?.find(
    (p) => p.projectId === formData.projectId,
  );
  const subtitle = [
    formData.name,
    formData.projectName || selectedProject?.projectName,
  ]
    .filter(Boolean)
    .join(" · ");

  /* ---------- small render helpers (markup only, same handlers) ---------- */
  const fieldBox = (key, label, required, control, err, full, boxSx) => (
    <Box key={key} sx={full ? { gridColumn: "1 / -1" } : boxSx}>
      {label && (
        <label style={lbl}>
          {label}
          {required && <span style={req}>*</span>}
        </label>
      )}
      {control}
      {err && (
        <span className="error-text" style={errText}>
          {err}
        </span>
      )}
    </Box>
  );

  const textInput = (field) =>
    fieldBox(
      field.name,
      field.label,
      REQUIRED_FIELDS.includes(field.name),
      <input
        className={`${errors[field.name] ? "input-error" : ""}`}
        type={field.type}
        placeholder={PLACEHOLDERS[field.name] || ""}
        value={formData[field.name] || ""}
        onChange={(e) => handleChange(field.name, e.target.value)}
        style={inp(!!errors[field.name])}
      />,
      errors[field.name],
    );

  const budgetInput = (field) =>
    fieldBox(
      field.name,
      field.label,
      false,
      <div style={boxWrap(false)}>
        <span style={prefix}>₹</span>
        <input
          type="number"
          value={formData[field.name] || ""}
          onChange={(e) => handleChange(field.name, e.target.value)}
          style={bareInp}
        />
      </div>,
    );

  const readOnlyInput = (field) =>
    fieldBox(
      field.name,
      field.label,
      false,
      <input
        type={field.type}
        value={formData[field.name] || ""}
        readOnly
        style={readOnlyInp}
      />,
    );

  const whatsappBox = (field) =>
    fieldBox(
      field.name,
      field.label,
      false,
      <label className="dc-wa" style={waBox(!!formData[field.name])}>
        <input
          type="checkbox"
          checked={!!formData[field.name]}
          onChange={(e) => handleChange(field.name, e.target.checked)}
          hidden
        />
        <span style={waTick(!!formData[field.name])}>
          {formData[field.name] ? "✓" : ""}
        </span>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          Reachable on WhatsApp
        </span>
      </label>,
    );

  // Phone + country code in one attached field (used for Phone and
  // Alternate Phone). Same handlers / state as before.
  const phoneWithCode = (field, country, setCountry, placeholder, newRow) =>
    fieldBox(
      field.name,
      field.label,
      REQUIRED_FIELDS.includes(field.name),
      <div style={boxWrap(!!errors[field.name])}>
        <div
          className="dc-cc"
          style={{
            display: "flex",
            alignItems: "center",
            flex: "0 0 auto",
            height: "100%",
            padding: "0 6px 0 10px",
            borderRight: `1px solid ${FIELD_EDGE}`,
            boxSizing: "border-box",
          }}
        >
          <CountrySelect value={country} onChange={(c) => setCountry(c)} />
        </div>
        <input
          type="tel"
          id={field.name}
          name={field.name}
          className={`dc-inp dc-bare ${errors[field.name] ? "input-error" : ""}`}
          placeholder={placeholder}
          inputMode="numeric"
          maxLength={10}
          value={formData[field.name] || ""}
          onChange={(e) => handlePhoneChange(field.name, e.target.value)}
          style={{ ...bareInp, fontFamily: FONT, fontSize: 13.5, paddingLeft: 10 }}
        />
      </div>,
      errors[field.name],
      false,
      // Alternate Phone starts the second row, under Lead Name.
      newRow ? { gridColumn: { lg: "1" } } : undefined,
    );

  const leadScoreField = (field) =>
    fieldBox(
      field.name,
      field.label,
      false,
      <Autocomplete
        name="leadScore"
        options={leadScoreOptions}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option.value.toString()}
        value={
          leadScoreOptions.find(
            (option) => option.value === formData[field.name],
          ) || null
        }
        onChange={(event, newValue) => {
          handleChange(field.name, newValue?.value || "");
        }}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderInput={(params) => (
          <TextField {...params} placeholder="Select Lead Score" />
        )}
        sx={acSx}
      />,
    );

  const priorityField = () =>
    fieldBox(
      "priority",
      "Priority",
      false,
      <Autocomplete
        name="priority"
        options={PRIORITY_OPTIONS}
        getOptionLabel={(option) => option.label}
        value={PRIORITY_OPTIONS.find((o) => o.value === formData.priority) || null}
        onChange={(event, newValue) =>
          handleChange("priority", newValue?.value || "")
        }
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderInput={(params) => (
          <TextField {...params} placeholder="Select Priority" />
        )}
        sx={acSx}
      />,
    );

  const inventoryField = () =>
    fieldBox(
      "inventoryIds",
      "Inventory",
      false,
      <Select
        multiple
        displayEmpty
        value={formData?.inventoryIds || []}
        onChange={(e) => handleChange("inventoryIds", e.target.value)}
        renderValue={(selected) =>
          selected.length === 0 ? (
            <span style={{ color: "#8A97A8" }}>Select inventory</span>
          ) : (
            selected
              .map((id) => {
                const inv = inventories?.find((i) => i.inventoryId === id);
                return inv ? inv.type : id;
              })
              .join(", ")
          )
        }
        sx={selSx}
      >
        {inventories?.map((inv) => (
          <MenuItem key={inv.inventoryId} value={inv.inventoryId}>
            <Checkbox
              checked={formData.inventoryIds?.includes(inv.inventoryId)}
            />
            <ListItemText
              primary={`${inv.type}${inv.floor ? ` (Floor ${inv.floor})` : ""}`}
            />
          </MenuItem>
        ))}
      </Select>,
    );

  const assignedField = (hideSelf) =>
    fieldBox(
      "assignedTo",
      "Assigned To",
      true,
      <Select
        className={`${errors.assignedTo ? "input-error" : ""}`}
        multiple
        displayEmpty
        value={formData?.assignedTo || []}
        onChange={(e) => handleChange("assignedTo", e.target.value)}
        renderValue={(selected) => {
          if (!selected || selected.length === 0) {
            return <span style={{ color: "#8A97A8" }}>Select team members</span>;
          }
          return selected
            .map((mobile) => {
              if (mobile === storedUser.username) return "Self";
              const usr = users?.find((u) => u.mobile === mobile);
              return usr ? usr.name : mobile;
            })
            .join(", ");
        }}
        error={!!errors.assignedTo}
        sx={selSx}
      >
        {users
          ?.filter((u) => (hideSelf ? u.mobile !== storedUser.username : true))
          .map((user) => (
            <MenuItem key={user.mobile} value={user.mobile}>
              <Checkbox
                checked={formData.assignedTo?.includes(user.mobile) || false}
              />
              <ListItemText
                primary={`${user?.name || "Name"} (${user.userRoleName})`}
              />
            </MenuItem>
          ))}
      </Select>,
      errors.assignedTo,
    );

  const leadSourceField = () =>
    fieldBox(
      "leadSource",
      "Lead Source",
      false,
      <Autocomplete
        options={sources || []}
        getOptionLabel={(option) => option.source || ""}
        value={sources?.find((p) => p.source === formData.leadSource) || null}
        onChange={(event, newValue) => {
          handleChange("leadSource", newValue?.source || "");
        }}
        isOptionEqualToValue={(option, value) =>
          option.sourceId === value.sourceId
        }
        renderInput={(params) => (
          <TextField {...params} placeholder="Select Lead Source" />
        )}
        sx={acSx}
      />,
    );

  const partnerField = () =>
    fieldBox(
      "partner",
      "Partner",
      false,
      <Autocomplete
        options={partners}
        getOptionLabel={(option) =>
          typeof option === "string"
            ? option
            : option.partnerName || option.companyName || ""
        }
        value={partners.find((p) => p.partnerId === formData.partnerId) || null}
        onChange={(event, newValue) => {
          if (newValue) {
            handleChange("partnerInput", newValue.partnerName);
            handleChange("partnerId", newValue.partnerId);
          } else {
            handleChange("partnerInput", "");
            handleChange("partnerId", null);
          }
        }}
        renderInput={(params) => (
          <TextField {...params} placeholder="Select Partner" />
        )}
        sx={acSx}
      />,
    );

  const nextFollowUpField = () =>
    fieldBox(
      "nextFollowUp",
      "Next Follow Up",
      false,
      <ThemedDatePicker
        name="nextFollowUp"
        value={formData.nextFollowUp || ""}
        onChange={(e) => handleChange("nextFollowUp", e.target.value)}
      />,
    );

  const statusField = () =>
    fieldBox(
      "status",
      "Status",
      false,
      <Autocomplete
        name="status"
        options={sortedStatus || []}
        getOptionLabel={(option) => option.status || ""}
        groupBy={(option) => option.metaLeadStatus.status}
        value={status?.find((p) => p.status === formData.status) || null}
        onChange={(e, value) => {
          setFormData({ ...formData, status: value?.status || "" });
        }}
        isOptionEqualToValue={(option, value) => option.status === value.status}
        renderInput={(params) => (
          <TextField {...params} placeholder="Select Status" />
        )}
        sx={acSx}
      />,
    );

  const commentField = () => (
    <Box key="remark" sx={{ gridColumn: "1 / -1" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.75,
        }}
      >
        <label htmlFor="" style={{ ...lbl, marginBottom: 0 }}>Comment</label>
        <Button
          size="small"
          startIcon={<AutoFixHighIcon sx={{ fontSize: "15px !important" }} />}
          onClick={handleWriteWithAI}
          disabled={aiLoading}
          sx={{
            textTransform: "none",
            fontSize: "12px",
            minWidth: 0,
            height: 26,
            px: 1,
            borderRadius: "8px",
            color: "#4A3FBF",
            background: "#EFEDFB",
            border: "1px solid #D3D0F2",
            "&:hover": { background: "#E7E4F8" },
          }}
        >
          {aiLoading ? "Writing..." : "Write with AI"}
        </Button>
      </Box>
      <textarea
        name="remark"
        rows={3}
        value={formData.remark || ""}
        onChange={(e) => handleChange("remark", e.target.value)}
        placeholder="Enter comment..."
        style={{
          ...inp(false),
          height: "auto",
          minHeight: 72,
          padding: "9px 12px",
          lineHeight: 1.5,
          resize: "vertical",
        }}
      />
    </Box>
  );

  const historyBlock = () =>
    remarks.length > 0 && (
      <Box key="history" sx={{ gridColumn: "1 / -1" }}>
        <label style={lbl}>History</label>
        {isMobile ? (
          remarks.map((r, idx) => (
            <div key={idx} className="card" style={historyCard}>
              <Typography sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: INK, mb: 1 }}>
                {r.commentBy === "ADMIN"
                  ? r?.organization?.adminName || "Admin"
                  : r.commentBy === ""
                    ? "System Comment"
                    : r.orgUser?.name || ""}
                <br />
                <span style={{ fontFamily: MONO, fontSize: 11.5, fontWeight: 500, color: MUTED }}>
                  {r.lastFollowUp === null ? "-" : formatDateTime(r?.createdDate)}
                </span>
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 500, color: INK, mb: 1.25, lineHeight: 1.5 }}>
                {r.remark}
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: MUTED, mb: 0.5 }}>
                Next Follow-up:{" "}
                <span style={{ fontFamily: MONO, color: INK_2, fontWeight: 600 }}>
                  {r.nextFollowUp === null ? "-" : formatDate(r?.nextFollowUp)}
                </span>
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: MUTED }}>
                Status: <span style={{ color: INK_2, fontWeight: 600 }}>{r.status}</span>
              </Typography>
            </div>
          ))
        ) : (
          <div
            style={{
              overflowX: "auto",
              width: "100%",
              maxHeight: 280,
              overflowY: "auto",
              border: `1px solid ${LINE}`,
              borderRadius: 10,
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontFamily: FONT,
                fontSize: "13px",
              }}
            >
              <thead>
                <tr>
                  <th style={thStyle}>Comment</th>
                  <th style={thStyle}>Comment By</th>
                  <th style={thStyle}>Last Follow-up</th>
                  <th style={thStyle}>Next Follow-up</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>

              <tbody>
                {remarks.map((r, idx) => (
                  <tr key={idx}>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{r.remark}</td>
                    <td style={tdStyle}>
                      {r.commentBy === "ADMIN"
                        ? r?.organization?.adminName || "Admin"
                        : r.commentBy === "webhook"
                          ? "System Comment"
                          : r.orgUser?.name || ""}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: MONO, fontSize: 12, whiteSpace: "nowrap" }}>
                      {r.lastFollowUp === null ? "-" : formatDateTime(r?.createdDate)}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: MONO, fontSize: 12, whiteSpace: "nowrap" }}>
                      {r.nextFollowUp === null ? "-" : formatDate(r?.nextFollowUp)}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: INK_2 }}>{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Box>
    );

  const sortedOptions = (field) =>
    field.options
      ?.slice()
      .sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate)) || [];

  const customFieldControl = (field) => (
    <Box key={field.fieldId}>
      <label style={lbl}>{field.label}</label>

      {/* SINGLE SELECT */}
      {field.type === "SINGLE_SELECT" && (
        <ThemedSelect
          value={formData[field.fieldName] || ""}
          onChange={(e) => handleChange(field.fieldName, e.target.value)}
          placeholder="Select"
          options={[
            { value: "", label: "Select" },
            ...sortedOptions(field).map((opt) => ({
              value: opt.optionId,
              label: opt.value,
            })),
          ]}
        />
      )}

      {/* MULTI SELECT */}
      {field.type === "MULTI_SELECT" && (
        <Select
          className="custom-select"
          multiple
          value={formData[field.fieldName] || []}
          // FIX: MUI Select returns the selected values in e.target.value
          // (there is no e.target.selectedOptions), so read the array directly.
          onChange={(e) =>
            handleChange(
              field.fieldName,
              typeof e.target.value === "string"
                ? e.target.value.split(",")
                : e.target.value,
            )
          }
          renderValue={(selected) =>
            selected
              .map(
                (id) =>
                  field.options?.find((o) => o.optionId === id)?.value || id,
              )
              .join(", ")
          }
          fullWidth
          sx={selSx}
        >
          {sortedOptions(field).map((opt) => (
            <MenuItem key={opt.optionId} value={opt.optionId}>
              <Checkbox
                checked={(formData[field.fieldName] || []).includes(opt.optionId)}
              />
              <ListItemText primary={opt.value} />
            </MenuItem>
          ))}
        </Select>
      )}

      {/* DATE */}
      {field.type === "DATE" && (
        <ThemedDatePicker
          value={formData[field.fieldName] || ""}
          onChange={(e) => handleChange(field.fieldName, e.target.value)}
        />
      )}

      {/* TEXT / NUMBER / BOOLEAN */}
      {!["SINGLE_SELECT", "MULTI_SELECT", "DATE"].includes(field.type) && (
        <input
          type={
            field.type === "NUMBER"
              ? "number"
              : field.type === "BOOLEAN"
                ? "checkbox"
                : "text"
          }
          value={
            field.type === "BOOLEAN" ? undefined : formData[field.fieldName] || ""
          }
          checked={
            field.type === "BOOLEAN"
              ? formData[field.fieldName] || false
              : undefined
          }
          onChange={(e) =>
            handleChange(
              field.fieldName,
              field.type === "BOOLEAN" ? e.target.checked : e.target.value,
            )
          }
          style={
            field.type === "BOOLEAN"
              ? { width: 18, height: 18, margin: "9px 0", accentColor: PRIMARY, cursor: "pointer" }
              : {
                  ...inp(false),
                  fontFamily: field.type === "NUMBER" ? MONO : FONT,
                  fontSize: field.type === "NUMBER" ? 13 : 13.5,
                }
          }
        />
      )}
    </Box>
  );

  /* ---------- section contents ---------- */
  const f = (name) => staticFieldMap[name];

  const contactItems = [
    show("name") && textInput(f("name")),
    show("phone") &&
      phoneWithCode(f("phone"), phoneCountry, setPhoneCountry, "Enter phone number", false),
    show("isWhatsapp") && whatsappBox(f("isWhatsapp")),
    show("email") && textInput(f("email")),
    show("alternatePhone") &&
      phoneWithCode(f("alternatePhone"), altPhoneCountry, setAltPhoneCountry, "Enter alternate phone", true),
    show("isAlternateWhatsapp") && whatsappBox(f("isAlternateWhatsapp")),
  ].filter(Boolean);

  const assignmentItems = [
    assignedField(!!access),
    leadScoreField(f("leadScore")),
    priorityField(),
  ];

  const requirementItems = [
    ...REQUIREMENT_FIELDS.filter(show).map((n) =>
      n === "minBudget" || n === "maxBudget" ? budgetInput(f(n)) : textInput(f(n)),
    ),
    showProfile("currentCity") && textInput(f("currentCity")),
    showProfile("currentCountry") && textInput(f("currentCountry")),
    inventoryField(),
  ].filter(Boolean);

  const followupItems = [
    historyBlock(),
    nextFollowUpField(),
    statusField(),
    commentField(),
  ].filter(Boolean);

  const attributionItems = [
    show("leadSource") && leadSourceField(),
    partnerField(),
    ...UTM_FIELDS.map((n) => readOnlyInput(f(n))),
  ].filter(Boolean);

  const customItems =
    access && inputFields?.customFields?.length > 0
      ? inputFields.customFields.map(customFieldControl)
      : [];

  const sections = [
    {
      id: "contact",
      title: "Contact",
      items: contactItems,
      cols: 4,
      meta: `${["name", "phone"].filter(show).length} required`,
    },
    { id: "assignment", title: "Assignment", items: assignmentItems, cols: 4, meta: "1 required" },
    { id: "requirement", title: "Requirement", items: requirementItems, cols: 5, meta: "optional" },
    {
      id: "followup",
      title: "Follow-up",
      items: followupItems,
      cols: 5,
      meta: remarks.length > 0 ? `${remarks.length} updates` : "optional",
      count: 3,
    },
    { id: "attribution", title: "Attribution", items: attributionItems, cols: 5, meta: "UTM is read-only" },
    { id: "custom", title: "Custom fields", items: customItems, cols: 5, meta: "optional" },
  ].filter((s) => s.items.length > 0);

  const sectionKey = sections.map((s) => s.id).join(",");

  // Highlight the nav item for the section in view
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) setActiveSection(en.target.dataset.sec);
        });
      },
      { rootMargin: "-25% 0px -65% 0px" },
    );
    Object.values(sectionRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [sectionKey]);

  const scrollToSection = (id) => {
    setActiveSection(id);
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderSection = (s) => (
    <section
      key={s.id}
      data-sec={s.id}
      ref={(el) => (sectionRefs.current[s.id] = el)}
      className="dc-card"
      style={card}
    >
      <div style={cardHead}>
        <span style={cardTitle}>
          <span style={dot(SEC_COLORS[s.id])} />
          {s.title}
        </span>
        <span style={cardMeta}>{s.meta}</span>
      </div>
      <Box sx={{ p: "16px 16px 18px" }}>
        <Box sx={cols(s.cols, s.lgTemplate)}>{s.items}</Box>
      </Box>
    </section>
  );

  const sectionNav = (
    <nav
      style={{
        position: "sticky",
        top: 16,
        background: "#FFFFFF",
        border: `1px solid ${LINE}`,
        borderRadius: 14,
        padding: 8,
      }}
    >
      {sections.map((s) => {
        const on = activeSection === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => scrollToSection(s.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              width: "100%",
              height: 36,
              padding: "0 10px",
              marginBottom: 2,
              border: 0,
              borderRadius: 9,
              background: on ? "#EAF2FB" : "transparent",
              cursor: "pointer",
              fontFamily: FONT,
              fontSize: 13.5,
              fontWeight: on ? 700 : 500,
              color: INK,
              textAlign: "left",
            }}
          >
            <span style={dot(SEC_COLORS[s.id], 7)} />
            <span style={{ flex: 1 }}>{s.title}</span>
            <span style={{ fontFamily: MONO, fontSize: 11, color: MUTED }}>
              {s.count ?? s.items.length}
            </span>
          </button>
        );
      })}
    </nav>
  );

  const cancelButton = (
    <Button onClick={goBack} sx={ghostBtn}>
      Cancel
    </Button>
  );

  const saveButton = (
    <Button
      className="btn-contained"
      onClick={handleEditLead}
      disabled={btnLoading}
      sx={primaryBtn}
    >
      {btnLoading ? "Updating..." : "Update lead"}
    </Button>
  );

  return (
    <ThemeProvider theme={formTheme}>
      <style>{SCOPED_CSS}</style>
      <div
        className="dc-editlead"
        style={{
          fontFamily: FONT,
          color: INK,
          padding: isMobile ? "4px 0 88px" : "4px 0 32px",
        }}
      >
        {/* ---------------- PAGE HEADER ---------------- */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 2,
            mb: 2.5,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <button
              type="button"
              onClick={goBack}
              style={{
                border: 0,
                background: "transparent",
                padding: 0,
                marginBottom: 14,
                cursor: "pointer",
                fontFamily: FONT,
                fontSize: 13,
                fontWeight: 600,
                color: PRIMARY,
              }}
            >
              ← Lead
            </button>
            <h1
              style={{
                margin: 0,
                fontFamily: FONT,
                fontSize: isMobile ? 24 : 28,
                fontWeight: 800,
                letterSpacing: "-0.03em",
                lineHeight: 1.15,
                color: INK,
              }}
            >
              Edit lead
            </h1>
            {subtitle && (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 13.5,
                  color: INK_2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
          {!isMobile && (
            <Box sx={{ display: "flex", gap: 1.25, flex: "0 0 auto" }}>
              {cancelButton}
              {saveButton}
            </Box>
          )}
        </Box>

        {/* ---------------- NAV + SECTIONS ---------------- */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: hideSectionNav
              ? "minmax(0,1fr)"
              : "200px minmax(0,1fr)",
            gap: "14px",
            alignItems: "start",
          }}
        >
          {!hideSectionNav && sectionNav}
          <div>{sections.map(renderSection)}</div>
        </Box>

        {/* ---------------- MOBILE ACTION BAR ---------------- */}
        {isMobile && (
          <Box
            className="btn-container"
            sx={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 30,
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              p: "10px 16px",
              background: "#FFFFFF",
              borderTop: `1px solid ${LINE}`,
              boxShadow: "0 -8px 24px -16px rgba(15,27,42,0.3)",
            }}
          >
            <ButtonBack handleBack={goBack} />
            <Box sx={{ flex: 1, display: "flex", "& > *": { flex: 1 } }}>
              {saveButton}
            </Box>
          </Box>
        )}

        <ErrorModal
          open={modalOpen}
          handleClose={() => setModalOpen(false)}
          msg={modalMsg}
          type={modalType}
        />
      </div>
    </ThemeProvider>
  );
};

export default EditLead;

const thStyle = {
  position: "sticky",
  top: 0,
  padding: "9px 12px",
  textAlign: "left",
  background: "#F7F9FC",
  borderBottom: `1px solid ${LINE}`,
  fontFamily: FONT,
  fontSize: "10.5px",
  fontWeight: 700,
  letterSpacing: "0.09em",
  whiteSpace: "nowrap",
  textTransform: "uppercase",
  color: MUTED,
};

const tdStyle = {
  padding: "10px 12px",
  verticalAlign: "top",
  background: "#FFFFFF",
  borderBottom: `1px solid ${LINE}`,
  color: INK,
  lineHeight: 1.5,
};

function formatDate(date) {
  if (!date) return;
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const formatDateTime = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  const formattedDate = date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const formattedTime = date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${formattedDate} ${formattedTime}`;
};